import { defineStore } from 'pinia';
import type { PagesConfig } from '@shared/types/PagesConfig';
import type { MenuSchema } from '@shared/types/MenuSchema';
import { useContextStore } from '@/Stores/context';
import type { DayPlannerConfig } from '@shared/types/DayPlanner';
import type { DetailConfigDocument } from '@shared/types/DetailConfig';
import type { ResultListFilterConfig } from '@shared/types/ResultListFilterConfig';
import type { QuicksearchFilterConfig } from '@shared/types/QuicksearchFilterConfig';
import { fetchMapConfig as requestMapConfig, type GenericMapConfig } from '@/Utils/Map/mapStyles';
import { fetchStoredDetailConfig } from '@/Utils/Detail/detailConfigApi';

type ExternalConfigStatus = 'idle' | 'loading' | 'loaded' | 'failed';

interface ConfigState {
  config: PagesConfig | null;
  dayPlannerConfig: DayPlannerConfig | null;
  menu: MenuSchema | null;
  externalDetailConfig: DetailConfigDocument | null;
  externalDetailConfigStatus: ExternalConfigStatus;
  externalDetailConfigPromise: Promise<void> | null;
  externalSearchFilterConfig: ResultListFilterConfig | null;
  externalSearchFilterConfigStatus: ExternalConfigStatus;
  externalSearchFilterConfigPromise: Promise<void> | null;
  externalQuicksearchFilterConfig: QuicksearchFilterConfig | null;
  externalQuicksearchFilterConfigStatus: ExternalConfigStatus;
  externalQuicksearchFilterConfigPromise: Promise<void> | null;
  mapConfig: GenericMapConfig | null;
  mapConfigStatus: ExternalConfigStatus;
  mapConfigPromise: Promise<void> | null;
  configPromise: Promise<void> | null;
  isLoading: boolean;
  error: string | null;
}

const configStoreCache = new Map<string, ReturnType<typeof createConfigStore>>();
const API_URL = import.meta.env.VITE_API_URL;

const createConfigStore = (experience: string) =>
  defineStore(`pages-config-${experience}`, {
    state: (): ConfigState => ({
      config: null,
      dayPlannerConfig: null,
      menu: null,
      externalDetailConfig: null,
      externalDetailConfigStatus: 'idle',
      externalDetailConfigPromise: null,
      externalSearchFilterConfig: null,
      externalSearchFilterConfigStatus: 'idle',
      externalSearchFilterConfigPromise: null,
      externalQuicksearchFilterConfig: null,
      externalQuicksearchFilterConfigStatus: 'idle',
      externalQuicksearchFilterConfigPromise: null,
      mapConfig: null,
      mapConfigStatus: 'idle',
      mapConfigPromise: null,
      configPromise: null,
      isLoading: false,
      error: null,
    }),
    actions: {
      async fetchConfig() {
        if (this.configPromise) {
          await this.configPromise;
          return;
        }

        const loadPromise = (async () => {
          this.resetExternalDetailConfig();
          this.resetExternalSearchFilterConfig();
          this.resetExternalQuicksearchConfig();
          await this.fetchData(`${API_URL}/config/${experience}`, 'config');
        })();

        this.configPromise = loadPromise;
        try {
          await loadPromise;
        } finally {
          this.configPromise = null;
        }
      },
      async fetchMenu() {
        await this.fetchData(`${API_URL}/menuSchema/${experience}`, 'menu');
      },
      async ensureMapConfigLoaded() {
        if (this.mapConfigStatus === 'loaded' || this.mapConfigStatus === 'failed') return;
        if (this.mapConfigStatus === 'loading' && this.mapConfigPromise) {
          await this.mapConfigPromise;
          return;
        }

        this.mapConfig = null;
        this.mapConfigStatus = 'loading';
        const loadPromise = (async () => {
          try {
            this.mapConfig = await requestMapConfig(experience);
            this.mapConfigStatus = 'loaded';
          } catch (error) {
            this.mapConfig = null;
            this.mapConfigStatus = 'failed';
            console.warn('[ConfigStore] Failed to load map configuration, using fallbacks', error);
          }
        })();
        this.mapConfigPromise = loadPromise;

        try {
          await loadPromise;
        } finally {
          this.mapConfigPromise = null;
        }
      },
      async fetchDayPlannerConfig() {
        const configUrl = String(this.config?.trip_plan_recommendations || '').trim();
        await this.fetchData(
          `${API_URL}/day-planner-config?url=${encodeURIComponent(configUrl)}`,
          'dayPlannerConfig',
        );
      },
      applyPreviewDetailConfig(document: DetailConfigDocument) {
        this.externalDetailConfig = document;
        this.externalDetailConfigStatus = 'loaded';
        this.externalDetailConfigPromise = null;
      },
      resetExternalDetailConfig() {
        this.externalDetailConfig = null;
        this.externalDetailConfigStatus = 'idle';
        this.externalDetailConfigPromise = null;
      },
      resetExternalSearchFilterConfig() {
        this.externalSearchFilterConfig = null;
        this.externalSearchFilterConfigStatus = 'idle';
        this.externalSearchFilterConfigPromise = null;
      },
      resetExternalQuicksearchConfig() {
        this.externalQuicksearchFilterConfig = null;
        this.externalQuicksearchFilterConfigStatus = 'idle';
        this.externalQuicksearchFilterConfigPromise = null;
      },
      async ensureExternalDetailConfigLoaded() {
        if (!this.config) {
          await this.fetchConfig();
        }
        if (!this.config) return;

        if (this.externalDetailConfigStatus === 'loaded') return;

        if (this.externalDetailConfigStatus === 'loading' && this.externalDetailConfigPromise) {
          await this.externalDetailConfigPromise;
          return;
        }

        const loadPromise = this.fetchExternalDetailConfig();
        this.externalDetailConfigPromise = loadPromise;

        try {
          await loadPromise;
        } finally {
          this.externalDetailConfigPromise = null;
        }
      },
      async fetchExternalDetailConfig() {
        if (!this.config) {
          this.externalDetailConfigStatus = 'idle';
          return;
        }

        const detailConfigUrl = this.config?.pages2?.detail_config?.trim();
        if (!detailConfigUrl) {
          this.externalDetailConfig = null;
          this.externalDetailConfigStatus = 'loaded';
          return;
        }

        this.externalDetailConfigStatus = 'loading';

        const result = await fetchStoredDetailConfig(detailConfigUrl);
        if (result.ok) {
          this.externalDetailConfig = result.document;
          this.externalDetailConfigStatus = 'loaded';
          return;
        }

        this.externalDetailConfig = null;
        this.externalDetailConfigStatus = 'failed';
        console.warn(
          '[ConfigStore] Failed to load external detail_config, using fallback',
          result.error,
        );
      },
      async ensureExternalSearchFilterConfigLoaded() {
        if (!this.config) {
          await this.fetchConfig();
        }
        if (!this.config) return;

        if (this.externalSearchFilterConfigStatus === 'loaded') return;

        if (
          this.externalSearchFilterConfigStatus === 'loading' &&
          this.externalSearchFilterConfigPromise
        ) {
          await this.externalSearchFilterConfigPromise;
          return;
        }

        const loadPromise = this.fetchExternalSearchFilterConfig();
        this.externalSearchFilterConfigPromise = loadPromise;

        try {
          await loadPromise;
        } finally {
          this.externalSearchFilterConfigPromise = null;
        }
      },
      async fetchExternalSearchFilterConfig() {
        if (!this.config) {
          this.externalSearchFilterConfigStatus = 'idle';
          return;
        }

        const searchFilterConfigUrl = this.config?.pages2?.search_filter_config?.trim();
        if (!searchFilterConfigUrl) {
          this.externalSearchFilterConfig = null;
          this.externalSearchFilterConfigStatus = 'loaded';
          return;
        }

        this.externalSearchFilterConfigStatus = 'loading';

        try {
          const response = await fetch(
            `${API_URL}/search-filter-config?url=${encodeURIComponent(searchFilterConfigUrl)}`,
          );
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          this.externalSearchFilterConfig = (await response.json()) as ResultListFilterConfig;
          this.externalSearchFilterConfigStatus = 'loaded';
        } catch (error) {
          this.externalSearchFilterConfig = null;
          this.externalSearchFilterConfigStatus = 'failed';
          console.warn(
            '[ConfigStore] Failed to load external search_filter_config, using fallback',
            error,
          );
        }
      },
      async ensureExternalQuicksearchConfigLoaded() {
        if (!this.config) await this.fetchConfig();
        if (!this.config || this.externalQuicksearchFilterConfigStatus === 'loaded') return;

        if (
          this.externalQuicksearchFilterConfigStatus === 'loading' &&
          this.externalQuicksearchFilterConfigPromise
        ) {
          await this.externalQuicksearchFilterConfigPromise;
          return;
        }

        const loadPromise = this.fetchExternalQuicksearchConfig();
        this.externalQuicksearchFilterConfigPromise = loadPromise;
        try {
          await loadPromise;
        } finally {
          this.externalQuicksearchFilterConfigPromise = null;
        }
      },
      async fetchExternalQuicksearchConfig() {
        if (!this.config) {
          this.externalQuicksearchFilterConfigStatus = 'idle';
          return;
        }

        const configUrl = this.config.pages2?.quicksearch_config?.trim();
        if (!configUrl) {
          this.externalQuicksearchFilterConfig = null;
          this.externalQuicksearchFilterConfigStatus = 'loaded';
          return;
        }

        this.externalQuicksearchFilterConfigStatus = 'loading';
        try {
          const response = await fetch(
            `${API_URL}/quicksearch-config?url=${encodeURIComponent(configUrl)}`,
          );
          if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
          this.externalQuicksearchFilterConfig = (await response.json()) as QuicksearchFilterConfig;
          this.externalQuicksearchFilterConfigStatus = 'loaded';
        } catch (error) {
          this.externalQuicksearchFilterConfig = null;
          this.externalQuicksearchFilterConfigStatus = 'failed';
          console.warn(
            '[ConfigStore] Failed to load external quicksearch_config, using fallback',
            error,
          );
        }
      },
      async fetchData(url: string, key: 'config' | 'menu' | 'dayPlannerConfig') {
        this.isLoading = true;
        this.error = null;

        try {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          this[key] = await response.json();
        } catch (error) {
          this.error = error instanceof Error ? error.message : 'Unbekannter Fehler';
          console.error(`Fehler beim Laden von ${key}:`, error);
        } finally {
          this.isLoading = false;
        }
      },
    },
  })();

export function useConfigStore() {
  const { experience } = useContextStore();
  if (!experience) throw new Error('No experience set in context store.');
  if (!configStoreCache.has(experience)) {
    configStoreCache.set(experience, createConfigStore(experience));
  }
  return configStoreCache.get(experience)!;
}
